/**
 * image-luminance — average perceived luminance of an image URL (0..1).
 *
 * Used to decide whether a brand logo is "light" (would vanish on a light
 * surface) and therefore needs a dark backdrop plate. Only opaque pixels count,
 * so a transparent PNG is judged by its visible ink, not its empty background.
 *
 * Returns null when it cannot be measured (load error, or a CORS-tainted canvas
 * that blocks getImageData) so callers can fall back to rendering the logo raw.
 * Results are cached per URL.
 */
const cache = new Map<string, Promise<number | null>>();

export function averageLuminance(url: string): Promise<number | null> {
  const cached = cache.get(url);
  if (cached) return cached;

  const promise = new Promise<number | null>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = 32;
        const h = 32;
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let lum = 0;
        let weight = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3]! / 255;
          if (a === 0) continue;
          const l = (0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!) / 255;
          lum += l * a;
          weight += a;
        }
        resolve(weight ? lum / weight : null);
      } catch {
        resolve(null); // tainted canvas / CORS — caller renders raw
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

  cache.set(url, promise);
  return promise;
}

// ponytail: demo self-check — run with `node --loader tsx image-luminance.ts` if needed.
// Pure-logic check lives in the luminance math above; DOM path is exercised by the app.
